// components/industry-templates/ManufacturingEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Factory, Cog, TrendingUp, Award } from 'lucide-react';

export const ManufacturingEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Manufacturing Tech */}
      {industryData?.manufacturing_tech && (
        <Card className="border-0 bg-gradient-to-br from-gray-600/20 to-slate-600/20 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Factory className="w-8 h-8" style={{ color: theme.primary }} />
              Manufacturing Technologies
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {industryData.manufacturing_tech.map((tech: any, idx: number) => (
                <div key={idx} className="p-5 bg-white/10 rounded-xl border border-white/20">
                  <div className="flex items-start gap-4">
                    <div className="text-3xl">⚙️</div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">{tech.name}</h3>
                      <p className="text-slate-300 text-sm mb-3">{tech.description}</p>
                      <div className="flex gap-2">
                        <Badge style={{ backgroundColor: theme.primary }}>{tech.category}</Badge>
                        {tech.live_demo && (
                          <Badge className="bg-green-500">Live Demo</Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Industry 4.0 */}
      {industryData?.industry_4_0 && (
        <Card className="border-2" style={{ borderColor: theme.primary, backgroundColor: 'rgba(75, 85, 99, 0.1)' }}>
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Cog className="w-6 h-6" style={{ color: theme.primary }} />
              Industry 4.0 Solutions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {industryData.industry_4_0.technologies?.map((tech: string, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg text-center border border-white/10">
                  <div className="text-2xl mb-2">🤖</div>
                  <p className="text-white font-medium text-sm">{tech}</p>
                </div>
              ))}
            </div>
            {industryData.industry_4_0.workshops && (
              <div className="mt-4 bg-white/5 rounded-lg p-4">
                <p className="text-white">
                  <span className="font-bold" style={{ color: theme.primary }}>
                    {industryData.industry_4_0.workshops}
                  </span> workshops on digital transformation
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Machinery Exhibition */}
      {industryData?.machinery_exhibition && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-6 h-6" style={{ color: theme.primary }} />
              Machinery & Equipment Exhibition
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {industryData.machinery_exhibition.map((machine: any, idx: number) => (
                <div key={idx} className="p-4 bg-white/5 rounded-lg border-l-4" style={{ borderColor: theme.primary }}>
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-bold text-white mb-1">{machine.name}</h4>
                      <p className="text-sm text-slate-400">{machine.manufacturer}</p>
                    </div>
                    <Badge style={{ backgroundColor: theme.primary }}>
                      {machine.category}
                    </Badge>
                  </div>
                  {machine.specifications && (
                    <p className="text-xs text-slate-500 mt-2">{machine.specifications}</p>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Supply Chain */}
      {industryData?.supply_chain && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Award className="w-6 h-6" style={{ color: theme.primary }} />
              Supply Chain Solutions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="bg-white/5 rounded-lg p-5">
              <p className="text-white mb-3">Optimize your supply chain with:</p>
              <div className="flex flex-wrap gap-2">
                {industryData.supply_chain.solutions?.map((solution: string, idx: number) => (
                  <Badge key={idx} variant="outline" className="border-white/20 text-white text-sm py-2 px-3">
                    {solution}
                  </Badge>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
